import { ReactNode } from 'react'

import favicon from '@/assets/icons/favicon.ico'
import MarketplaceOgImage from '@/assets/images/marketplace-og.png'
import { isPublic } from '@/utils/environment'
import { formatPrice } from '@/utils/products'

const canonicalUrl = process.env.GATSBY_CANONICAL_URL
const productLabel = process.env.GATSBY_PRODUCT_LABEL

interface Props {
  description?: string
  defaultTitle?: string
  title?: string
  image?: string
  url?: string
  twitterUsername?: string
  type?: string
  typeLabel?: string
  priceAmount?: number
  priceCurrency?: string
  schemaMarkup?: string
  path?: string
  noIndex?: boolean
  children?: ReactNode
}

const SEO = ({
  description = `${productLabel} Marketplace – go beyond the basic. Transform the way you work with powerful apps and services.`,
  defaultTitle = `${productLabel} Marketplace`,
  title = `${productLabel} Marketplace`,
  image = process.env.GATSBY_PRODUCT_WEBSITE_URL + MarketplaceOgImage,
  url,
  twitterUsername = '@LiveChat',
  type,
  typeLabel,
  priceAmount,
  priceCurrency,
  schemaMarkup,
  path = '',
  noIndex,
  children,
}: Props) => {
  const hasOgUrl = url || canonicalUrl

  return (
    <>
      <html lang="en" />

      <title itemProp="name" lang="en">
        {title || defaultTitle}
      </title>

      <meta property="ui-version" content={process.env.GATSBY_GIT_SHA} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1, shrink-to-fit=no"
      />

      <meta name="description" content={description} />
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description} />
      <meta itemProp="image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterUsername} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:creator" content={twitterUsername} />
      <meta name="twitter:image" content={image} />

      {priceAmount && (
        <meta
          name="twitter:data1"
          content={formatPrice(priceAmount).toString()}
        />
      )}
      {priceAmount && <meta name="twitter:label1" content="Price" />}

      {type && <meta name="twitter:data2" content={type} />}
      {type && <meta name="twitter:label2" content={typeLabel} />}

      <meta property="og:title" content={title} />
      <meta property="og:type" content="article" />
      {hasOgUrl && (
        <meta property="og:url" content={url || `${canonicalUrl}${path}`} />
      )}
      <meta property="og:image" content={image} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={productLabel} />
      {priceAmount && (
        <meta
          property="og:price:amount"
          content={formatPrice(priceAmount).toString()}
        />
      )}
      {priceAmount && (
        <meta property="og:price:currency" content={priceCurrency} />
      )}
      <meta property="og:type" content="article" />

      {canonicalUrl && <link rel="canonical" href={`${canonicalUrl}${path}`} />}

      {schemaMarkup && (
        <script type="application/ld+json">{schemaMarkup}</script>
      )}

      {favicon && <link rel="shortcut icon" href={favicon} />}

      {/* Only Public LiveChat Marketplace is indexed by crawlers */}
      {isPublic && noIndex && (
        <meta name="robots" content="noindex, nofollow" />
      )}
      {children}
    </>
  )
}

export default SEO
